import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PiArrowsClockwiseLight, PiInfoLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import {
  getReviewPlaybookStatus,
  rerunReviewFromPlaybook,
  ReviewPlaybookStatus,
} from '../../utils/reviewSourceApi';

/**
 * "Playbook updated" banner for a review session (Epic B, item 5).
 *
 * Passively checks lineage on open (no queuing) and, when the session's playbook
 * has gained or changed clauses, offers a one-click SCOPED re-review that re-runs
 * ONLY those clauses across the session's existing documents. Renders nothing
 * unless the session is genuinely behind - no noise on an up-to-date review.
 */
interface Props {
  sessionUuid: string;
  /** Called after a successful re-run enqueue with the live group, so the parent
   *  can join PubSub and show progress + auto-refresh on completion. */
  onRerunQueued?: (pubsub?: { group: string; correlation_id: string }) => void;
  /** Bump to re-check after an external change. */
  refreshKey?: number | string;
}

const ReviewPlaybookBanner: React.FC<Props> = ({ sessionUuid, onRerunQueued, refreshKey }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [state, setState] = useState<ReviewPlaybookStatus | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sessionUuid) { setState(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const s = await getReviewPlaybookStatus(sessionUuid);
        if (!cancelled) setState(s);
      } catch {
        if (!cancelled) setState(null); // silent - a banner must never break the page
      }
    })();
    return () => { cancelled = true; };
  }, [sessionUuid, refreshKey]);

  const handleRerun = useCallback(async () => {
    setBusy(true);
    try {
      const res = await rerunReviewFromPlaybook(sessionUuid);
      if (res.status === 'processing') {
        dispatch(showFeedbackModal({
          showModal: true, modalType: 'success', duration: 4000,
          message: t('Re-reviewing the changed clauses against the latest playbook…'),
        }));
        setState((prev) => (prev ? { ...prev, status: 'up_to_date' } : prev));
        onRerunQueued?.(res.pubsub);
      } else {
        // Already current (raced with another re-run) - clear the banner.
        setState((prev) => (prev ? { ...prev, status: 'up_to_date' } : prev));
      }
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: t('Could not re-run from the playbook. Please try again.'),
      }));
    } finally {
      setBusy(false);
    }
  }, [sessionUuid, dispatch, t, onRerunQueued]);

  if (!state || state.status !== 'behind') return null;

  const n = state.changed_clause_count || 0;

  return (
    <div className="flex-shrink-0 flex items-center gap-3 px-6 py-3 border-b border-info-200 bg-info-50 text-info-900 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
      <PiInfoLight size={18} className="shrink-0 text-info-600" />
      <p className="text-sm flex-1">
        {n > 0
          ? t('The playbook was updated — {{n}} clause(s) changed. Re-review just those clauses against your documents; existing findings are kept.', { n })
          : t('The playbook this review used has been updated. Re-review the changed clauses against your documents — only those are re-run.')}
      </p>
      <Button
        variant="primary"
        size="small"
        onClick={handleRerun}
        isLoading={busy}
        icon={<PiArrowsClockwiseLight size={14} />}
      >
        {t('Re-run changed clauses')}
      </Button>
    </div>
  );
};

export default ReviewPlaybookBanner;
